import React from 'react';

const statusColors = {
  success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  error: 'bg-rose-100 text-rose-800 border-rose-200',
  info: 'bg-blue-100 text-blue-800 border-blue-200',
  neutral: 'bg-slate-100 text-slate-800 border-slate-200',
  primary: 'bg-indigo-100 text-indigo-800 border-indigo-200',
};

const statusDotColors = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  error: 'bg-rose-500',
  info: 'bg-blue-500',
  neutral: 'bg-slate-500',
  primary: 'bg-indigo-500',
};

const sizes = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

const Badge = ({
  children,
  status = 'neutral',
  size = 'md',
  variant = 'pill', // 'pill' or 'square'
  dot = false,
  removable = false,
  onRemove,
  icon: Icon,
  className = '',
  ...props
}) => {
  const colorClasses = statusColors[status] || statusColors.neutral;
  const sizeClasses = sizes[size] || sizes.md;
  const shapeClasses = variant === 'pill' ? 'rounded-full' : 'rounded-md';

  return (
    <span
      className={`
        inline-flex items-center font-medium border
        ${colorClasses}
        ${sizeClasses}
        ${shapeClasses}
        ${className}
      `}
      {...props}
    >
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${statusDotColors[status] || statusDotColors.neutral}`}
        />
      )}
      {Icon && <Icon className="w-3.5 h-3.5 mr-1" />}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-1.5 -mr-0.5 h-4 w-4 rounded-full inline-flex items-center justify-center hover:bg-black/10 focus:outline-none"
        >
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </span>
  );
};

// Status Badge with specific styling for contract statuses
const StatusBadge = ({ status, className = '' }) => {
  const statusMap = {
    draft: { label: 'Draft', color: 'neutral' },
    preview: { label: 'Preview', color: 'info' },
    pending_payment: { label: 'Pending Payment', color: 'warning' },
    paid: { label: 'Paid', color: 'success' },
    finalized: { label: 'Finalized', color: 'success' },
    expired: { label: 'Expired', color: 'error' },
    cancelled: { label: 'Cancelled', color: 'error' },
  };

  const { label, color } = statusMap[status] || { label: status, color: 'neutral' };

  return (
    <Badge status={color} dot size="sm" className={className}>
      {label}
    </Badge>
  );
};

Badge.Status = StatusBadge;

export default Badge;
